import { motion } from "framer-motion";
import { Layers, Zap, DollarSign, Wrench, Shield, Home } from "lucide-react";
import KOPipelineStep from "./KOPipelineStep";
import { adGroups } from "./mockData";

const iconMap: Record<string, React.ReactNode> = {
  Zap: <Zap className="h-6 w-6" />,
  DollarSign: <DollarSign className="h-6 w-6" />,
  Wrench: <Wrench className="h-6 w-6" />,
  Shield: <Shield className="h-6 w-6" />,
  Home: <Home className="h-6 w-6" />,
};

const groupColors = [
  { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", icon: "bg-blue-100 text-blue-600", pill: "bg-blue-100 text-blue-700" },
  { bg: "bg-green-50", border: "border-green-200", text: "text-green-700", icon: "bg-green-100 text-green-600", pill: "bg-green-100 text-green-700" },
  { bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-700", icon: "bg-purple-100 text-purple-600", pill: "bg-purple-100 text-purple-700" },
  { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", icon: "bg-amber-100 text-amber-600", pill: "bg-amber-100 text-amber-700" },
  { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", icon: "bg-rose-100 text-rose-600", pill: "bg-rose-100 text-rose-700" },
];

const KOThematicGrouping = () => {
  return (
    <section className="py-24 px-4 bg-background">
      <div className="container mx-auto max-w-6xl">
        <KOPipelineStep step={4} title="Organise Into Thematic Ad Groups">
          {/* Value prop connector */}
          <motion.div
            className="flex items-center gap-3 mb-8 bg-primary/5 rounded-xl p-4 border border-primary/10"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Layers className="h-6 w-6 text-primary flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-primary">Grouped by Your Value Proposition</p>
              <p className="text-xs text-muted-foreground">
                AI analyses your landing page and brand messaging to cluster keywords into themes
                that match searcher intent — boosting Quality Score and lowering actual CPC.
              </p>
            </div>
          </motion.div>

          {/* Ad group cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {adGroups.map((group, index) => {
              const colors = groupColors[index % groupColors.length];
              return (
                <motion.div
                  key={group.name}
                  className={`rounded-xl p-6 border ${colors.bg} ${colors.border}`}
                  initial={{ opacity: 0, y: 30, scale: 0.95 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.12,
                    type: "spring",
                    stiffness: 200,
                  }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.icon}`}>
                      {iconMap[group.icon]}
                    </div>
                    <div>
                      <h4 className={`font-bold ${colors.text}`}>{group.name}</h4>
                      <p className="text-xs text-muted-foreground">
                        {group.keywords.length} keywords · ${group.avgCPC.toFixed(2)} avg CPC
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {group.keywords.map((kw) => (
                      <span
                        key={kw}
                        className={`text-xs px-2.5 py-1 rounded-full ${colors.pill}`}
                      >
                        {kw}
                      </span>
                    ))}
                  </div>

                  <div className="flex gap-4 text-xs text-muted-foreground pt-3 border-t border-border/50">
                    <span>~{group.estImpressions.toLocaleString()} imp/mo</span>
                    <span>~{group.estClicks} clicks/mo</span>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* SEO text */}
          <motion.p
            className="mt-8 text-muted-foreground leading-relaxed max-w-4xl"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            Thematic ad group organisation is what separates effective campaigns from wasted spend.
            When keywords in an ad group share a common theme, your ad copy can speak directly to
            what the searcher is looking for. Google rewards this relevance with higher Quality Scores,
            which directly reduce your cost-per-click — sometimes by 50% or more. Upfixe's AI creates
            five strategically differentiated groups: emergency/urgent keywords, cost-conscious terms,
            specific service types, maintenance-related searches, and location-based queries. Each group
            gets tailored ad copy generated from your value proposition document.
          </motion.p>
        </KOPipelineStep>
      </div>
    </section>
  );
};

export default KOThematicGrouping;
